import { getPosthogIfReady, initPosthogDeferred } from "config/posthog-runtime";
import {
  fetchPosthogConversationsRemoteConfig,
  isAdBlockVulnerablePosthogApiHost,
  resolvePosthogClientHosts,
} from "config/posthog-client";
import type { PostHogWithConversations } from "types/posthog-conversations";

import { isPosthogConversationsPanelOpen } from "./posthogConversationsDom";

const hostnameMatchesAllowedDomain = (hostname: string, allowedDomain: string): boolean => {
  const normalized = allowedDomain
    .replace(/^https?:\/\//, "")
    .split("/")[0]
    .split("?")[0]
    .split(":")[0];

  if (!normalized) {
    return false;
  }

  if (normalized.startsWith("*.")) {
    const root = normalized.slice(2);
    return hostname.endsWith(`.${root}`) || hostname === root;
  }

  return hostname === normalized;
};

export const getPosthogSupportUnavailableMessage = async (): Promise<string | null> => {
  if (typeof window === "undefined") {
    return null;
  }

  if (isPosthogConversationsPanelOpen()) {
    return null;
  }

  try {
    const remote = await fetchPosthogConversationsRemoteConfig();
    const hostname = window.location.hostname;
    const posthog =
      (getPosthogIfReady() as PostHogWithConversations | null) ??
      ((await initPosthogDeferred()) as PostHogWithConversations | null);
    const sdkAvailable = Boolean(posthog?.conversations?.isAvailable());

    if (remote === false) {
      return "PostHog Support is off for this project key. In PostHog → Support → Settings, enable conversations on the same project as REACT_APP_POSTHOG_KEY.";
    }

    if (remote.enabled !== true) {
      return "PostHog Support conversations are disabled for this project. Enable them in PostHog → Support → Settings.";
    }

    const domains = remote.domains ?? [];
    if (domains.length > 0) {
      const allowed = domains.some((domain) => hostnameMatchesAllowedDomain(hostname, domain));
      if (!allowed) {
        return `This hostname (${hostname}) is not in PostHog Support allowed domains. Add it in PostHog → Support → In-app widget.`;
      }
    }

    if (!sdkAvailable) {
      return `PostHog Support is enabled for this project key, but the chat module has not loaded in the browser yet. Hard-refresh (Cmd+Shift+R), use http://localhost:3001 (not 127.0.0.1), and confirm traffic goes through same-origin /ph (not us.i.posthog.com). Hostname: ${hostname}.`;
    }

    if (posthog?.conversations && !posthog.conversations.isVisible()) {
      return `PostHog Support could not open the widget on ${hostname}. Confirm allowed domains include this host and hard-refresh after changing REACT_APP_POSTHOG_KEY.`;
    }
  } catch (error) {
    const { apiHost } = resolvePosthogClientHosts();
    if (isAdBlockVulnerablePosthogApiHost(apiHost)) {
      return `PostHog Support request failed. The browser likely blocked analytics hosts. Use the API proxy api_host, e.g. "https://api.rivetai.com/ph" (current: ${apiHost || "unset"}).`;
    }
    if (error instanceof Error && error.message.includes("returned non-JSON")) {
      return `Same-origin PostHog proxy at ${apiHost} is not forwarding (got HTML/SPA). Set REACT_APP_POSTHOG_API_HOST to the API proxy (https://api.<env>.rivetai.com/ph), or for local dev confirm the Vite /ph proxy is running.`;
    }
    console.warn("[RivetAI Support] Failed to probe PostHog remote config:", error);
  }

  return null;
};

export const logPosthogSupportDiagnostics = async (): Promise<string | null> => {
  const message = await getPosthogSupportUnavailableMessage();
  if (message) {
    console.warn(`[RivetAI Support] ${message}`);
  }
  return message;
};
